import { useRef, useState } from "react";
import { CheckCircle2, Loader2Icon, UploadIcon } from "lucide-react";
import { Button, DialogFooter, cn, useMultiStepForm } from "web-design-system";
import {
  useInvestorDocuments,
  useUploadInvestorDocument,
} from "creso-core/hooks";
import type { InvestorDocument, InvestorDocumentType } from "creso-core/types";
import { handleError } from "../../utils";
import { useClientOnboarding } from "./context";
import { useMinorOnboarding } from "./minor-form-data";

const ACCEPTED_TYPES = ["application/pdf", "image/jpeg", "image/png"];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

type DocumentSlot = { id: InvestorDocumentType; title: string; hint: string };

function DocumentRow({
  slot,
  document,
  isUploading,
  onSelect,
}: {
  slot: DocumentSlot;
  document: InvestorDocument | undefined;
  isUploading: boolean;
  onSelect: (file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-1">
      <div
        className={cn(
          "flex items-center gap-3 rounded-lg border border-border p-3",
          document && "bg-tag-green border-tag-green-foreground/30"
        )}
      >
        {isUploading ? (
          <Loader2Icon className="size-4 shrink-0 animate-spin" />
        ) : document ? (
          <CheckCircle2 className="size-4 shrink-0 text-tag-green-foreground" />
        ) : (
          <UploadIcon className="size-4 shrink-0" />
        )}
        <div className="flex flex-col flex-1 min-w-0">
          <span className="text-sm font-medium">{slot.title}</span>
          <span className="text-xs text-muted-foreground truncate">
            {document ? (document.file_name ?? "Uploaded") : slot.hint}
          </span>
        </div>
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0"
          disabled={isUploading}
          onClick={() => inputRef.current?.click()}
        >
          {document ? "Replace" : "Upload"}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_TYPES.join(",")}
          className="hidden"
          onChange={(e) => {
            const selected = e.target.files?.[0];
            e.target.value = "";
            if (!selected) return;
            if (!ACCEPTED_TYPES.includes(selected.type)) {
              setError("Upload a PDF, JPEG or PNG");
              return;
            }
            if (selected.size > MAX_FILE_SIZE) {
              setError("File must be 10 MB or smaller");
              return;
            }
            setError(null);
            onSelect(selected);
          }}
        />
      </div>
      {!!error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}

/** Minor onboarding step 6: proofs for the minor (and court order if needed) */
export function Documents() {
  const { client } = useClientOnboarding();
  const { previousStep, nextStep } = useMultiStepForm();
  const { guardian } = useMinorOnboarding();
  const investmentAccountGuid = client?.investment_account ?? "";
  const { data: documents } = useInvestorDocuments(investmentAccountGuid);
  const { mutateAsync: uploadDocument } = useUploadInvestorDocument();
  const [uploading, setUploading] = useState<InvestorDocumentType | null>(null);

  const slots: DocumentSlot[] = [
    {
      id: "MINOR_ID_PROOF",
      title: "Minor's ID proof",
      hint: "Birth certificate or Aadhaar",
    },
    {
      id: "CANCELLED_CHEQUE",
      title: "Cancelled cheque",
      hint: "Or a bank statement",
    },
    ...(guardian?.relationship === "court_appointed_legal_guardian"
      ? [
          {
            id: "COURT_ORDER" as const,
            title: "Court order",
            hint: "Appointing the legal guardian",
          },
        ]
      : []),
  ];

  const documentFor = (type: InvestorDocumentType) =>
    documents?.find((d) => d.document_type === type);
  const allUploaded = slots.every((slot) => documentFor(slot.id));

  // Uploading the same type again replaces it
  const handleSelect = async (type: InvestorDocumentType, file: File) => {
    try {
      setUploading(type);
      await uploadDocument({
        investmentAccountId: investmentAccountGuid,
        documentType: type,
        file,
      });
    } catch (error) {
      handleError(error, "Failed to upload document");
    } finally {
      setUploading(null);
    }
  };

  return (
    <div className="flex flex-col size-full p-6 gap-6">
      <span className="text-lg font-semibold">Documents</span>
      {/* Padding keeps focus rings from being clipped by the scroll area */}
      <div className="flex flex-col flex-1 gap-3 overflow-y-auto -m-1 p-1">
        {slots.map((slot) => (
          <DocumentRow
            key={slot.id}
            slot={slot}
            document={documentFor(slot.id)}
            isUploading={uploading === slot.id}
            onSelect={(file) => handleSelect(slot.id, file)}
          />
        ))}
        <span className="text-xs text-muted-foreground">
          PDF, JPEG or PNG, up to 10 MB each
        </span>
      </div>
      <DialogFooter className="flex sm:flex-col gap-4">
        <div className="flex items-center w-full gap-5">
          <Button type="button" variant="outline" onClick={previousStep}>
            Previous
          </Button>
          <Button
            className="flex-1"
            type="button"
            disabled={!allUploaded || !!uploading}
            onClick={() => nextStep()}
          >
            Done
          </Button>
        </div>
      </DialogFooter>
    </div>
  );
}
